import Box from "@mui/material/Box";
import InputFile from "common/components/Elements/Form/InputFile/InputFile";
import InputRadioGroup from "common/components/Elements/Form/InputRadioGroup/InputRadioGroup";
import ModalActions from "common/components/Elements/Modal/ModalActions/ModalActions";
import SimpleModal from "common/components/Elements/Modal/SimpleModal/SimpleModal";
import type { FileMetaData } from "common/utilities/browser/readFile/readFile";
import {
  enqueueInfoNotification,
  enqueueWarningNotification,
} from "core/redux/actions/notification/notificationActions";
import type { AppDispatch } from "core/redux/store/configureStore.types";
import type { ValueLabelOption } from "core/utilities/core.types";
import I18n from "i18n/en-US";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getSession, uploadFile } from "sequenceAnalyser/api/api";
import type { EntitySequence as Sequence, SequenceAlign } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { HandleInsertSequences } from "sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";
import {
  generateSequenceQualifiedId,
  highestQualifiedIdForManuallyInsertedSequences,
  SequenceQualifiedIdTypes,
} from "sequenceAnalyser/utilities/selectionActions/selectionActions";

export interface ComponentProps {
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  open: boolean;
  onOpen: (open: boolean) => void;
  onInsertSequences: HandleInsertSequences;
  selection: SequenceSelection;
  sessionId: string;
}

export function InsertFromFileModal({
  open,
  onOpen,
  alignedSequences,
  onInsertSequences,
  selection,
  sessionId,
  sequences,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [processing, setProcessing] = useState(false);

  // Position
  const lastSelectedId = selection.ids[selection.ids.length - 1];
  const lastId = alignedSequences.length;

  const insertPositions: ValueLabelOption[] = [{ value: "0", label: "Beginning" }];

  if (lastSelectedId) {
    const id = alignedSequences.findIndex((s) => s.id === lastSelectedId) + 1;

    if (lastId !== id) {
      insertPositions.push({
        value: id.toString(),
        label: "After last selected sequence",
      });
    }
  }

  if (alignedSequences.length > 0) {
    insertPositions.push({ value: alignedSequences.length.toString(), label: "End" });
  }

  const [insertPosition, setInsertPosition] = React.useState(insertPositions[0].value);

  // File types
  const fileTypes: ValueLabelOption[] = [
    { value: "all", label: "All" },
    { value: "fasta", label: "FASTA" },
    { value: "msf", label: "MSF" },
  ];

  const fileTypesExtensions = {
    all: ["*"],
    fasta: [".fa", ".faa", ".fasta"],
    msf: [".msf"],
  };

  type FileType = keyof typeof fileTypesExtensions;

  const [fileType, setFileType] = useState<FileType>("all");

  // File
  const [startUpload, setStartUpload] = useState(false);
  const [file, setFile] = useState<{ metaData: FileMetaData; data?: string } | undefined>();

  const handleChange = (metaData: FileMetaData, data?: string) => {
    if (metaData && data) {
      setFile({ metaData, data });
    } else if (metaData) {
      setFile({ metaData });
    } else {
      setFile(undefined);
    }
  };

  const processUploadFile = useCallback(
    async (newFileMetaData: FileMetaData, newFileData: string) => {
      try {
        const output = await uploadFile(sessionId, {
          content: newFileData,
          name: newFileMetaData.name,
          encoding: "base64",
        });

        const { sequences: fileSequences, alignedSequences: fileAlignedSequences } = output;

        let nextId =
          highestQualifiedIdForManuallyInsertedSequences(SequenceQualifiedIdTypes.ManuallyInserted, sequences) + 1;

        // qualifiedId returned by the API is a very long id, we are overriding this
        // using the same format as when inserting sequences manually
        const fileSequencesWithGeneratedQualifiedId: Sequence[] = fileSequences.map((s) => {
          const nextQualifiedId = generateSequenceQualifiedId(SequenceQualifiedIdTypes.ManuallyInserted, [], nextId);
          nextId += 1;
          return { ...s, qualifiedId: nextQualifiedId };
        });

        const sessionResponse = await getSession(sessionId);

        if (!sessionResponse.payload?.sequenceType) {
          dispatch(enqueueWarningNotification("Could not set sequence Type. Please try to reload the page"));
        }

        onInsertSequences(
          fileSequencesWithGeneratedQualifiedId,
          fileSequencesWithGeneratedQualifiedId,
          fileAlignedSequences,
          Number(insertPosition),
          sessionResponse.payload?.sequenceType,
        );

        dispatch(enqueueInfoNotification(I18n.SequenceAnalyser.InsertFromFileModal.success));
      } finally {
        // Close modal
        onOpen(false);
      }
    },
    [dispatch, insertPosition, onInsertSequences, onOpen, sequences, sessionId],
  );

  useEffect(() => {
    if (file?.data && !processing) {
      setProcessing(true);
      processUploadFile(file.metaData, file.data);
    }
  }, [file, processUploadFile, processing]);

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.InsertFromFileModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      content={
        <Box mt={1}>
          <InputRadioGroup
            label="Position to insert sequences"
            options={insertPositions}
            onChange={(value) => setInsertPosition(value)}
            value={insertPosition}
            horizontal
            modalLabel
          />

          <InputRadioGroup
            label="File type"
            options={fileTypes}
            onChange={(value) => setFileType(value as FileType)}
            value={fileType}
            horizontal
            modalLabel
          />

          <InputFile
            label="File"
            fileTypes={fileTypesExtensions[fileType]}
            onChange={handleChange}
            startUpload={startUpload}
            modalLabel
          />

          <ModalActions
            applyText={I18n.SequenceAnalyser.InsertFromFileModal.apply}
            disableApply={startUpload || processing || !file}
            persisting={startUpload}
            onApply={() => {
              setStartUpload(true);
            }}
          />
        </Box>
      }
    />
  );
}

export default InsertFromFileModal;
